import React, { FC } from "react";
import { useIntl, MessageDescriptor } from "react-intl";
import { Button } from "@natera/form";
import { messages } from "./messages";
import { PublicGisLookupErrorType } from "./types";

interface ErrorContent {
  title: MessageDescriptor;
  body: MessageDescriptor;
  /** Whether the patient can retry the lookup (vs. a hard terminal state). */
  retryable: boolean;
}

const ERROR_CONTENT: Record<PublicGisLookupErrorType, ErrorContent> = {
  MP_NO_PATIENT_MATCH: {
    title: messages.errorNoPatientTitle,
    body: messages.errorNoPatientBody,
    retryable: true,
  },
  MP_NO_ORDER_FOUND: {
    title: messages.errorNoOrderTitle,
    body: messages.errorNoOrderBody,
    retryable: false,
  },
  MP_NO_MATCHING_ORDER: {
    title: messages.errorNoOrderTitle,
    body: messages.errorNoOrderBody,
    retryable: false,
  },
  MP_MULTIPLE_ORDERS: {
    title: messages.errorMultipleTitle,
    body: messages.errorMultipleBody,
    retryable: false,
  },
  MP_ACTIVE_ALREADY_EXISTS: {
    title: messages.errorApptExistsTitle,
    body: messages.errorApptExistsBody,
    retryable: false,
  },
  MP_GENERIC_NO_MATCH_ERROR: {
    title: messages.errorGenericTitle,
    body: messages.errorGenericBody,
    retryable: true,
  },
};

interface TerminalScreenProps {
  errorType: PublicGisLookupErrorType;
  onRetry: () => void;
}

/**
 * Renders the GuestMPErrors terminal states for the order-lookup funnel.
 * Retryable states (no patient match / generic) offer a "try again" action;
 * hard terminal states (no order / multiple / appointment exists) do not, in
 * line with the single-order requirement (no order picker).
 */
const TerminalScreen: FC<TerminalScreenProps> = ({ errorType, onRetry }) => {
  const intl = useIntl();
  const content =
    ERROR_CONTENT[errorType] ?? ERROR_CONTENT.MP_GENERIC_NO_MATCH_ERROR;

  return (
    <div className="public-gis-lookup__terminal" role="alert">
      <h1 className="public-gis-lookup__terminal-title">
        {intl.formatMessage(content.title)}
      </h1>
      <p className="public-gis-lookup__terminal-body">
        {intl.formatMessage(content.body)}
      </p>
      {content.retryable && (
        <Button unelevated onClick={onRetry}>
          {intl.formatMessage(messages.tryAgain)}
        </Button>
      )}
    </div>
  );
};

export default TerminalScreen;
